import React, { useCallback, useState } from "react";
import { IBook } from "@/store/models/books.models";
import eyeIcon from "@/assets/icons/eye.svg";
import {
  useDeleteBookMutation,
  useGetGeneratedBooksQuery,
} from "@/store/rtk-api/books.api";
import { Pagination, Skeleton } from "@mui/material";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import trashIcon from "@/assets/icons/trash.svg";
import bookImage from "@/assets/images/book.png";
import styles from "./GeneratedBooks.module.scss";
import { Profile } from "@/store/models/users.models";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import { ViewBookModal } from "@/pages/BooksPage/components/ViewBookModal/ViewBookModal";

type Props = {
  profile: Profile;
};

const GeneratedBooks = ({ profile }: Props) => {
  const [page, setPage] = useState(1);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenViewModal, setIsOpenViewModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState<IBook | null>(null);
  const itemsPerPage = useItemsPerPage(2, 4, 6, 1050, 1470);

  const {
    data: booksData,
    refetch,
    isLoading,
    isSuccess,
  } = useGetGeneratedBooksQuery({
    page,
    limit: itemsPerPage,
    profileUid: profile.uid,
  });

  const [deleteBookByUid] = useDeleteBookMutation();

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleViewModal = (book: IBook) => {
    setSelectedBook(book);
    setIsOpenViewModal((prev) => !prev);
  };

  const handleToggleDeleteModal = (book: IBook) => {
    setSelectedBook(book);
    setIsOpenDeleteModal(true);
  };

  const handleDeleteBook = useCallback(async () => {
    if (selectedBook) {
      await deleteBookByUid(selectedBook.uid);
      setIsOpenDeleteModal(false);
      refetch();
    }
  }, [selectedBook, deleteBookByUid, refetch]);

  return (
    <div className={styles.content}>
      <div className={styles.itemsContainer}>
        {isLoading &&
          Array(3)
            .fill(0)
            .map((_, index) => (
              <>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={200}
                  animation="wave"
                  sx={{ borderRadius: "8px" }}
                />
              </>
            ))}
        {isSuccess && booksData && booksData.total === 0 && (
          <div className={styles.noDataText}>
            There is no data about generated books
          </div>
        )}
        {isSuccess &&
          booksData.stories.map((book) => (
            <div className={styles.item} key={book.uid}>
              <img
                className={styles.itemBookImg}
                src={book.imgs.length ? book.imgs[0] : bookImage}
                alt="book"
              />
              <p className={styles.itemDescr}>{book.description}</p>
              <div className={styles.itemBtns}>
                <button
                  className={styles.itemReviewBtn}
                  onClick={() => handleToggleViewModal(book)}
                >
                  <img
                    className={styles.itemBtnImg}
                    src={eyeIcon}
                    alt="review"
                  />
                </button>
                <button
                  className={styles.itemDeleteBtn}
                  onClick={() => handleToggleDeleteModal(book)}
                >
                  <img
                    className={styles.itemBtnImg}
                    src={trashIcon}
                    alt="delete"
                  />
                </button>
              </div>
            </div>
          ))}
        {isOpenDeleteModal && (
          <DeleteModal
            isOpen={isOpenDeleteModal}
            setIsOpen={setIsOpenDeleteModal}
            name={
              selectedBook && selectedBook.title ? selectedBook.title : "book"
            }
            supportingText="Are you sure you want to delete this book?"
            onDelete={handleDeleteBook}
          />
        )}

        {isOpenViewModal && selectedBook && (
          <ViewBookModal
            isOpen={isOpenViewModal}
            setIsOpen={setIsOpenViewModal}
            selectedBook={selectedBook}
          />
        )}
      </div>
      {isSuccess && (
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={Math.ceil(booksData.total / itemsPerPage)}
        />
      )}
    </div>
  );
};

export default GeneratedBooks;

